import type { ReactNode } from 'react';
import { useState } from 'react';
import AppShell from '../components/AppShell';
import Button from '../components/Button';
import Field from '../components/Field';
import Select from '../components/Select';
import NumberInput from '../components/NumberInput';
import MultiSelect from '../components/MultiSelect';
import Card from '../components/Card';
import Callout from '../components/Callout';
import Stepper from '../components/Stepper';
import Wordmark from '../components/Wordmark';

function Section({
  title,
  note,
  children,
}: {
  title: string;
  note?: string;
  children: ReactNode;
}) {
  return (
    <section className="border-t border-grey-200 pt-10">
      <h2 className="t-h2 text-ink">{title}</h2>
      {note && <p className="t-meta measure mt-3 text-grey-600">{note}</p>}
      <div className="mt-7">{children}</div>
    </section>
  );
}

/** Ported verbatim from the custom properties published on
 *  etrenziksmarthome.com. The contrast column is measured against the ground
 *  each colour is actually used on. */
const SWATCHES: { name: string; hex: string; use: string; onDark?: boolean }[] = [
  { name: 'ink', hex: '#0B0B0C', use: 'Headings and dark sections — 19.7:1 on white' },
  { name: 'ink-800', hex: '#1A1B1D', use: 'Body text — 17.2:1' },
  { name: 'grey-600', hex: '#55595B', use: 'Muted text — 7.08:1, the lightest text allowed' },
  { name: 'grey-500', hex: '#74797B', use: 'Large text and UI only — 4.41:1' },
  { name: 'grey-300', hex: '#C9CDCE', use: 'Control borders' },
  { name: 'grey-200', hex: '#E2E5E6', use: 'Dividers and card edges' },
  { name: 'grey-050', hex: '#F7F8F8', use: 'Alternating section ground' },
  { name: 'green-600', hex: '#35812A', use: 'The accent — buttons, selection — 4.86:1' },
  { name: 'green-700', hex: '#2C6B24', use: 'Hover and green body text — 6.49:1' },
  { name: 'green-500', hex: '#4FA83A', use: 'Rules and mark strokes — 3.0:1, never text' },
  { name: 'green-400', hex: '#7CC35C', use: 'Green text on dark — 9.2:1 on ink', onDark: true },
  { name: 'green-050', hex: '#EFF8EA', use: 'Selected chip ground' },
  { name: 'alert', hex: '#B3261E', use: 'Errors — added here, see DECISIONS.md' },
];

export default function Styleguide() {
  const [attendance, setAttendance] = useState<number | ''>(400);
  const [wants, setWants] = useState<string[]>(['fellowship']);
  const [org, setOrg] = useState('');
  const [showError, setShowError] = useState(false);

  return (
    <AppShell action={<span className="t-meta text-grey-600">Style guide</span>}>
      <p className="t-eyebrow text-green-600">Design system</p>
      <h1 className="t-h1 mt-3 text-ink">Style guide</h1>
      <p className="t-body-lg measure mt-4 text-grey-600">
        Every component in the system. The palette, type, radii, and shadows are ported
        from etrenziksmarthome.com so the two properties read as one company.
      </p>

      <div className="mt-14 flex flex-col gap-14">
        <Section
          title="Colour"
          note="Taken from the custom properties published on the Smart Home site, not sampled from a screenshot. Contrast is measured against the ground each colour is actually used on."
        >
          <ul className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            {SWATCHES.map((s) => (
              <li
                key={s.name}
                className="flex items-center gap-4 rounded-lg border border-grey-200 bg-white p-4 shadow-sm"
              >
                <span
                  className="flex h-14 w-14 shrink-0 items-center justify-center rounded-sm border border-ink/10"
                  style={{ background: s.onDark ? '#0B0B0C' : s.hex }}
                  aria-hidden="true"
                >
                  {s.onDark && (
                    <span
                      className="h-7 w-7 rounded-full"
                      style={{ background: s.hex }}
                    />
                  )}
                </span>
                <span className="min-w-0">
                  <span className="t-h3 block text-ink">{s.name}</span>
                  <span className="t-numeral t-meta block text-grey-600">{s.hex}</span>
                  <span className="t-meta block text-grey-600">{s.use}</span>
                </span>
              </li>
            ))}
          </ul>
        </Section>

        <Section
          title="Type"
          note="Inter, with the weights and negative tracking the Smart Home site uses. Body sits at 17px on a 1.65 line, same as theirs."
        >
          <div className="flex flex-col gap-5 rounded-lg border border-grey-200 bg-white p-7 shadow-sm">
            <p className="t-eyebrow text-green-600">Eyebrow label</p>
            <p className="t-display text-ink">Display</p>
            <p className="t-h1 text-ink">Heading one</p>
            <p className="t-h2 text-ink">Heading two</p>
            <p className="t-h3 text-ink">Heading three</p>
            <p className="t-body-lg measure text-grey-600">
              Large body. Answer a few questions, pick the buildings that catch your eye,
              and get concept images you can take to a design professional.
            </p>
            <p className="t-body measure text-grey-600">
              Body. The measure is held under seventy characters so a paragraph stays
              readable on a phone and on a projector at the back of a room.
            </p>
            <p className="t-meta text-grey-600">Meta and captions.</p>
            <p className="t-numeral text-2xl text-ink">
              18,400 sf &nbsp;·&nbsp; 214 stalls &nbsp;·&nbsp; 4.6 acres
            </p>
            <p className="text-lg">
              <Wordmark />
            </p>
          </div>
        </Section>

        <Section
          title="Buttons"
          note="Pills in the accent green, matching theirs. Sentence case, and the label says what happens."
        >
          <div className="flex flex-col gap-6">
            <div className="flex flex-wrap items-center gap-3">
              <Button>See my concepts</Button>
              <Button variant="secondary">Go back</Button>
              <Button variant="quiet">Save and finish later</Button>
              <Button disabled>Pick 5 to continue</Button>
            </div>
            <div className="max-w-sm">
              <p className="t-meta mb-2 text-grey-600">Full width below 640px:</p>
              <Button block>Send my request</Button>
            </div>
          </div>
        </Section>

        <Section title="Stepper">
          <Stepper
            steps={['Building type', 'Questions', 'Size', 'System', 'Styles', 'Concepts']}
            current={2}
          />
        </Section>

        <Section title="Fields">
          <div className="grid gap-8 sm:grid-cols-2">
            <Field
              id="sg-name"
              label="Your name"
              hint="We only use this to address the brief."
              placeholder="Jane Whitfield"
            />
            <Field
              id="sg-org"
              label="Organization"
              hint="Church, company, school system, or county."
              placeholder="Grace Fellowship"
              value={org}
              onChange={(e) => setOrg(e.target.value)}
              error={showError && !org ? 'Enter the name of your organization.' : undefined}
            />
            <Select
              id="sg-timeline"
              label="When do you want to be in the building?"
              hint="A rough answer is fine."
              placeholder="Choose a timeline"
              options={[
                { value: 'asap', label: 'As soon as possible' },
                { value: '1y', label: 'Within a year' },
                { value: '2y', label: 'One to two years' },
                { value: 'later', label: 'Further out than that' },
              ]}
            />
            <NumberInput
              id="sg-attendance"
              label="Average weekly attendance"
              hint="Add up every service on a normal weekend."
              value={attendance}
              onValueChange={setAttendance}
              min={0}
              step={25}
              suffix="people"
            />
          </div>
          <div className="mt-6">
            <Button variant="secondary" onClick={() => setShowError((v) => !v)}>
              {showError ? 'Hide the error state' : 'Show the error state'}
            </Button>
          </div>
        </Section>

        <Section
          title="Chips"
          note="Selection is a green inset rule on a pale green ground. No scale transform and no checkmark overlay."
        >
          <MultiSelect
            legend="What else goes in the building?"
            hint="Pick everything you know you want. You can change this later."
            options={[
              { value: 'fellowship', label: 'Fellowship hall' },
              { value: 'kitchen', label: 'Commercial kitchen' },
              { value: 'gym', label: 'Gym or family life center' },
              { value: 'classrooms', label: 'Classrooms' },
              { value: 'offices', label: 'Offices' },
              { value: 'cafe', label: 'Coffee bar' },
              { value: 'nursery', label: 'Nursery' },
            ]}
            value={wants}
            onChange={setWants}
            max={5}
          />
        </Section>

        <Section title="Cards" note="14px radius, hairline edge, and the Smart Home shadow-sm.">
          <div className="grid gap-4 sm:grid-cols-2">
            <Card
              title="Pre-engineered metal"
              figure="$190–280 /sf"
              footer={<Button variant="secondary">Choose this system</Button>}
            >
              The fastest and least expensive shell. The look comes from what you put on
              it — masonry at the base, storefront glass at the entry, a standing-seam
              roof.
            </Card>
            <Card title="Program" figure="18,400 sf">
              Sanctuary, lobby, a children&rsquo;s wing, and offices, with a fellowship
              hall you can add in a later phase.
            </Card>
          </div>
        </Section>

        <Section title="Callouts">
          <div className="flex flex-col gap-4">
            <Callout title="Why we ask">
              Attendance drives the size of the room, and the room drives almost
              everything else — parking, restrooms, and how much land you need.
            </Callout>
            <Callout tone="confirm" title="Your answers are saved">
              You can close this and come back to it later from the same link.
            </Callout>
            <Callout tone="caution" title="This site may be tight">
              The program needs about 4.6 acres and you told us you have 3.2. You could go
              to two stories, or move the fellowship hall into a later phase.
            </Callout>
          </div>
        </Section>

        <Section
          title="Dark section"
          note="The Smart Home hero runs on ink with green-400 for the accent type. The same treatment is available here for hero bands and CTA strips."
        >
          <div className="rounded-lg bg-ink px-7 py-10">
            <p className="t-eyebrow text-green-400">Residential &amp; commercial</p>
            <p className="t-h2 mt-3 text-white">Every system in your building.</p>
            <p className="t-body measure mt-4 text-grey-300">
              Green type on ink measures 9.2:1, and grey-300 body copy measures 12.3:1.
            </p>
          </div>
        </Section>
      </div>
    </AppShell>
  );
}
